import { useState, useCallback, useEffect, useRef } from "react";
import { PAGINATION_INITIAL_STATE } from "@hyro-components/hyro-table/HyroTable.consts";
import { FILTER_OPERATORS_DATA } from "@hyro-components/hyro-filter/HyroFilter.consts";
import { FIELD_TYPES } from "@hyro-components/fields/utils";

const EMPTY_ARRAY = [];

export const useServerPagination = ({
  initialFilters = EMPTY_ARRAY,
  handlePaginationParamsChange,
  data = EMPTY_ARRAY,
  snapshotKey
}) => {
  const [searchText, setSearchText] = useState("");
  const [filters, setFilters] = useState(initialFilters);
  const [filtersToSend, setFiltersToSend] = useState([]);
  const [paginationModel, setPaginationModel] = useState(PAGINATION_INITIAL_STATE);
  const [sortModel, setSortModel] = useState([]);
  const timeNow = useRef(new Date().toISOString());
  const lastSnapshotKey = useRef(snapshotKey);

  const buildURLSearchParams = useCallback((overrides = {}) => {
    const urlParams = new URLSearchParams();

    const currentPagination = overrides.paginationModel || paginationModel;
    const currentSearch = overrides.searchText !== undefined ? overrides.searchText : searchText;
    const currentSort = overrides.sortModel || sortModel;
    const currentFilters = overrides.filters || filtersToSend;

    urlParams.append("fromDate", timeNow.current);
    if (currentPagination?.page !== undefined) {
      urlParams.append("pageNumber", currentPagination.page + 1);
    }
    if (currentPagination?.pageSize) {
      urlParams.append("pageSize", currentPagination.pageSize);
    }
    if (currentSearch) {
      urlParams.append("search", currentSearch);
    }
    if (currentSort?.[0]?.field) {
      urlParams.append("sortBy", currentSort[0].field);
    }
    if (currentSort?.[0]?.sort) {
      urlParams.append("sortOrder", currentSort[0].sort);
    }
    if (currentFilters?.length) {
      currentFilters.forEach((filter) => {
        urlParams.append("filterKey", filter.key);
        urlParams.append("filterOperator", filter.operator || FILTER_OPERATORS_DATA.CONTAINS.key);
        urlParams.append("filterValue", filter.value);
      });
    }

    return urlParams.toString();
  }, [paginationModel, searchText, sortModel, filtersToSend]);

  // Pagination
  const onPaginationModelChange = useCallback((updPaginationModel) => {
    setPaginationModel(updPaginationModel);
    const urlParams = buildURLSearchParams({ paginationModel: updPaginationModel });
    handlePaginationParamsChange?.(urlParams);
  }, [buildURLSearchParams, handlePaginationParamsChange]);

  // Sort
  const onSortModelChange = useCallback((updSortModel) => {
    setSortModel(updSortModel);
    const urlParams = buildURLSearchParams({ sortModel: updSortModel });
    handlePaginationParamsChange?.(urlParams);
  }, [buildURLSearchParams, handlePaginationParamsChange]);

  // Search
  useEffect(() => {
    const urlParams = buildURLSearchParams({ searchText });
    handlePaginationParamsChange?.(urlParams);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  // Filters
  useEffect(() => {
    const newFiltersToSend = filters
      .filter((filter) => filter.isActive && filter.value)
      .flatMap((f) => {
        if (Array.isArray(f.value) && f.value.length === 2 && !Array.isArray(f.value[0])) {
          if (f.type === FIELD_TYPES.DATE) {
            return [
              {
                key: f.key,
                value: f.value[0].utc(),
                operator: FILTER_OPERATORS_DATA.GREATER_THAN_OR_EQUAL_TO.key
              },
              {
                key: f.key,
                value: f.value[1].utc(),
                operator: FILTER_OPERATORS_DATA.LESS_THAN_OR_EQUAL_TO.key
              }
            ];
          }
        }

        return f.value.map((valueItem) => ({
          key: f.key,
          value: valueItem?.key ?? valueItem,
          operator: f.operator || FILTER_OPERATORS_DATA.CONTAINS.key
        }));
      });

    if (JSON.stringify(filtersToSend) === JSON.stringify(newFiltersToSend)) return;

    setFiltersToSend(newFiltersToSend);
    const urlParams = buildURLSearchParams({ filters: newFiltersToSend });
    handlePaginationParamsChange?.(urlParams);
  }, [buildURLSearchParams, handlePaginationParamsChange, filters, filtersToSend]);

  // `fromDate` is sent as an upper bound — the server applies it as `createdAt <= fromDate` so that
  // rows created mid-session cannot shift pages underneath the user while paging. That means it has
  // to be moved forward deliberately, or a refresh re-asks the same question and can never return
  // anything newer than the moment the table mounted. Re-emitting the params is enough on its own:
  // the emitted string changes, so a consumer keying its query on it refetches once.
  const renewSnapshot = () => {
    timeNow.current = new Date().toISOString();
    handlePaginationParamsChange?.(buildURLSearchParams());
  };

  // Lets an owner pull in rows it just created without remounting the table, which would reset the
  // refresh button's auto-refresh toggle along with the page, sort and filters.
  useEffect(() => {
    if (snapshotKey === lastSnapshotKey.current) return;
    lastSnapshotKey.current = snapshotKey;
    renewSnapshot();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshotKey]);

  return {
    searchText,
    setSearchText,
    filters,
    setFilters,
    paginationModel,
    onPaginationModelChange,
    sortModel,
    onSortModelChange,
    renewSnapshot,
    rows: data || []
  };
};
